import { useCallback, useEffect, useRef, useState } from 'react';
import {
  MAX_ATTACHMENTS,
  MAX_ATTACHMENT_BYTES,
  detectFileType,
} from '../lib/attachments';
import { WidgetApiError, isAbortError } from '../lib/errors';
import { createId } from '../lib/ids';
import type { ComposerAttachment } from '../types/chat';

/** Uploads one file; resolves with the storage path and the session it belongs to. */
export type UploadFn = (
  file: File,
  extension: string,
  onProgress: (fraction: number) => void,
  signal: AbortSignal,
) => Promise<{ path: string; sessionId: string }>;

/** Inline composer message (BR-05 / BR-17). `key` is under `attachment.*`. */
export interface AttachmentNotice {
  key:
    | 'typeNotAllowed'
    | 'tooLarge'
    | 'limit'
    | 'rateLimited'
    | 'disabled'
    | 'uploadFailed';
  name?: string;
}

function noticeForUploadError(error: unknown, name: string): AttachmentNotice {
  const code = error instanceof WidgetApiError ? error.code : '';
  if (code === 'file_type_not_allowed') return { key: 'typeNotAllowed', name };
  if (code === 'file_too_large') return { key: 'tooLarge', name };
  if (code === 'upload_rate_limited') return { key: 'rateLimited' };
  if (code === 'file_upload_disabled') return { key: 'disabled' };
  return { key: 'uploadFailed', name };
}

/**
 * Composer file queue — in memory only, like chat messages. Each file uploads
 * as soon as it's picked. Owns the preview object URLs until the files are
 * sent (`takeAttachments` hands them to the message).
 */
export function useAttachments(upload: UploadFn) {
  const [attachments, setAttachments] = useState<ComposerAttachment[]>([]);
  const [notice, setNotice] = useState<AttachmentNotice | null>(null);
  const attachmentsRef = useRef<ComposerAttachment[]>([]);
  const controllersRef = useRef(new Map<string, AbortController>());
  const uploadRef = useRef(upload);
  useEffect(() => {
    uploadRef.current = upload;
  }, [upload]);

  const commit = useCallback((next: ComposerAttachment[]) => {
    attachmentsRef.current = next;
    setAttachments(next);
  }, []);

  const patch = useCallback(
    (id: string, changes: Partial<ComposerAttachment>) => {
      commit(
        attachmentsRef.current.map((a) =>
          a.id === id ? { ...a, ...changes } : a,
        ),
      );
    },
    [commit],
  );

  const startUpload = useCallback(
    (item: ComposerAttachment) => {
      controllersRef.current.get(item.id)?.abort();
      const controller = new AbortController();
      controllersRef.current.set(item.id, controller);
      patch(item.id, { status: 'uploading', progress: 0 });

      uploadRef
        .current(
          item.file,
          item.extension,
          (progress) => patch(item.id, { progress }),
          controller.signal,
        )
        .then(({ path, sessionId }) => {
          if (controller.signal.aborted) return;
          patch(item.id, { status: 'done', progress: 1, path, sessionId });
        })
        .catch((error: unknown) => {
          if (controller.signal.aborted || isAbortError(error)) return;
          patch(item.id, { status: 'failed' });
          setNotice(noticeForUploadError(error, item.file.name));
        })
        .finally(() => {
          if (controllersRef.current.get(item.id) === controller) {
            controllersRef.current.delete(item.id);
          }
        });
    },
    [patch],
  );

  const addAttachments = useCallback(
    async (files: File[]) => {
      setNotice(null);
      let rejected: AttachmentNotice | null = null;
      const accepted: ComposerAttachment[] = [];

      for (const file of files) {
        if (file.size > MAX_ATTACHMENT_BYTES) {
          rejected ??= { key: 'tooLarge', name: file.name };
          continue;
        }
        const type = await detectFileType(file).catch(() => null);
        if (!type || file.size === 0) {
          rejected ??= { key: 'typeNotAllowed', name: file.name };
          continue;
        }
        accepted.push({
          id: createId('file'),
          file,
          kind: type.kind,
          extension: type.extension,
          previewUrl:
            type.kind === 'image' ? URL.createObjectURL(file) : undefined,
          status: 'uploading',
          progress: 0,
        });
      }

      // Re-read after the awaits: another pick may have landed meanwhile.
      const room = MAX_ATTACHMENTS - attachmentsRef.current.length;
      const added = accepted.slice(0, Math.max(0, room));
      accepted.slice(added.length).forEach((a) => {
        if (a.previewUrl) URL.revokeObjectURL(a.previewUrl);
      });
      if (accepted.length > added.length) rejected ??= { key: 'limit' };

      if (rejected) setNotice(rejected);
      if (added.length === 0) return;
      commit([...attachmentsRef.current, ...added]);
      added.forEach(startUpload);
    },
    [commit, startUpload],
  );

  /** iOS native picker skips encoding files over the limit; it only sends the name. */
  const reportTooLarge = useCallback((name: string) => {
    setNotice({ key: 'tooLarge', name });
  }, []);

  const retryAttachment = useCallback(
    (id: string) => {
      const target = attachmentsRef.current.find((a) => a.id === id);
      if (!target || target.status !== 'failed') return;
      setNotice(null);
      startUpload(target);
    },
    [startUpload],
  );

  const removeAttachment = useCallback(
    (id: string) => {
      const current = attachmentsRef.current;
      const target = current.find((a) => a.id === id);
      if (!target) return;
      controllersRef.current.get(id)?.abort();
      controllersRef.current.delete(id);
      if (target.previewUrl) URL.revokeObjectURL(target.previewUrl);
      setNotice(null);
      commit(current.filter((a) => a.id !== id));
    },
    [commit],
  );

  /** Empties the queue and hands the files (and their preview URLs) to the caller. */
  const takeAttachments = useCallback((): ComposerAttachment[] => {
    const taken = attachmentsRef.current;
    if (taken.length > 0) commit([]);
    setNotice(null);
    return taken;
  }, [commit]);

  const clearAttachments = useCallback(() => {
    controllersRef.current.forEach((c) => c.abort());
    controllersRef.current.clear();
    setNotice(null);
    if (attachmentsRef.current.length === 0) return;
    attachmentsRef.current.forEach((a) => {
      if (a.previewUrl) URL.revokeObjectURL(a.previewUrl);
    });
    commit([]);
  }, [commit]);

  useEffect(() => {
    const controllers = controllersRef.current;
    return () => {
      controllers.forEach((c) => c.abort());
      attachmentsRef.current.forEach((a) => {
        if (a.previewUrl) URL.revokeObjectURL(a.previewUrl);
      });
    };
  }, []);

  return {
    attachments,
    notice,
    addAttachments,
    reportTooLarge,
    removeAttachment,
    retryAttachment,
    takeAttachments,
    clearAttachments,
  };
}
